import React, { useEffect, useRef, useState } from 'react';
import './TourSpotlight.css';

// TourSpotlight — SHE POINTS AT THE REAL THING. 2026-08-31.
//
// Braeden: "she can show what she can do and what her featrues are by opening
// and pointing to stuff, this box down here is like an updated google search
// bar, find anything online, click on the dashboard and you can create a new
// project and i can help do just about anything you want"
//
// So this puts a ring around an element that is ACTUALLY ON THEIR SCREEN
// while she talks about it. Two rules, both learned the hard way elsewhere in
// this app:
//
//   1. Never draw a ring around something that is not there. If the selector
//      finds nothing, nothing is drawn and her words stand alone - the same
//      rule the assist pointer follows against the accessibility tree.
//   2. Never block the thing being pointed at. The overlay is pointer-events:
//      none, so if they want to click the box she is describing, they can -
//      mid-sentence, which is exactly the interruption he wants honoured.

const TARGETS = {
  search: ['.input-row', 'input.input'],
  dashboard: ['.dashboard-menu-btn', '.aria-hamburger'],
  project: ['.dash-project--new', '.dashboard-menu-btn'],
  // the members-only desktop download, top right (stage three of the intro, 9/5)
  download: ['.download-corner-pill--win', '.download-corner-pill', '.download-pill'],
};

export default function TourSpotlight({ point, onDone }) {
  const [box, setBox] = useState(null);
  // onDone is a fresh arrow every parent render. With it in the effect deps
  // the effect re-ran on EVERY render: the 9s timer restarted, the 400ms
  // re-measure restarted, and a measure taken while the drawer was still
  // sliding in nulled the box - measured 9/2: the New project ring never
  // appeared. The callback lives in a ref; the effect keys on `point` only.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const lastGoodRef = useRef(null);

  useEffect(() => {
    lastGoodRef.current = null;
    if (!point || !point.target) { setBox(null); return undefined; }
    const selectors = TARGETS[point.target] || [point.target];

    const measure = () => {
      // FIRST VISIBLE MATCH, NOT FIRST MATCH (Braeden 9/2: "when she's talking,
      // she's not pointing like she should be"). Measured on howtoaria.com:
      // `.dashboard-menu-btn` is in the DOM but display:none on the Ask page
      // (0x0), so the loop matched it, measured nothing, and gave up without
      // ever reaching `.aria-hamburger` sitting visibly at 14,6. She said
      // "click the burger-looking thing" and drew no ring, every time.
      // ...and ON SCREEN. The drawer keeps a collapsed-rail copy of New
      // project in the DOM with real size and a negative x; it matched
      // first and the ring came out a 24px sliver at the left edge (9/2,
      // measured during the walkthrough). Every match is tried, and the
      // first one that is sized AND inside the viewport wins.
      const vw = window.innerWidth, vh = window.innerHeight;
      let r = null;
      for (const s of selectors) {
        let els = [];
        try { els = Array.from(document.querySelectorAll(s)); } catch (_) { els = []; }
        for (const el of els) {
          const rr = el.getBoundingClientRect();
          if (rr.width && rr.height && rr.right > 8 && rr.bottom > 8 && rr.left < vw - 8 && rr.top < vh - 8) {
            // THE RING FITS THE SHAPE (Braeden 9/3: "the assist mode feature
            // needs to fit the shape of what it's giving attention to,
            // perfectly"). Take the element's own corner radius with the box.
            let radius = 0;
            try { radius = parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0; } catch (_) { radius = 0; }
            r = { top: rr.top, left: rr.left, width: rr.width, height: rr.height, radius };
            break;
          }
        }
        if (r) break;
      }
      if (!r) {
        // Off-screen for a beat (a drawer sliding in): keep the last good
        // box rather than blinking the ring out; only a target that has
        // truly gone takes it down.
        // ...but a target gone for more than 1.2s HAS truly gone (the
        // hamburger unmounts when the drawer opens; its ring hung over the
        // drawer header for six seconds on 9/3).
        if (lastGoodRef.current && Date.now() - lastGoodRef.current.at < 1200) return;
        lastGoodRef.current = null;
        setBox(null); return;
      }
      const nb = { top: r.top, left: r.left, width: r.width, height: r.height, radius: r.radius || 0 };
      lastGoodRef.current = { ...nb, at: Date.now() };
      setBox((prev) => (prev && prev.top === nb.top && prev.left === nb.left && prev.width === nb.width && prev.height === nb.height) ? prev : nb);
    };

    measure();
    // The layout moves under her: the chat sheet opens, the pad appears, the
    // window resizes. A ring that stays where the element USED to be is worse
    // than no ring, so it re-measures instead of drawing once.
    const iv = setInterval(measure, 400);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    // It comes down on its own. A ring left up after she has moved on turns
    // into decoration, and decoration is what makes people stop reading it.
    const off = setTimeout(() => { setBox(null); try { if (onDoneRef.current) onDoneRef.current(); } catch (_) {} }, 12000);
    return () => {
      clearInterval(iv); clearTimeout(off);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [point]);

  if (!point || !box) return null;

  // ── OVER THE WHOLE SCREEN, AND ALL OF IT VISIBLE (Braeden 9/2) ─────────
  // "the visual generator didn't look right, it needs to go over the screen
  // and it didn't, couldn't see it all the way." Two faults, both measured
  // on the menu button at 14,6: the ring's top edge sat at -4px (clipped by
  // the viewport), and z-index 70 lost to the drawer and the chat sheet. So:
  // the ring is clamped inside the viewport, the whole page is dimmed
  // through a cut-out (a spotlight, not a decoration), the layer sits above
  // everything the app draws, and the caption goes wherever there is room.
  // Hugs the element: 3px outside its edge, its own corner radius plus that
  // (a circle stays a circle, a pill stays a pill), 2px stroke. 9/3.
  const pad = 3;
  const vw = window.innerWidth, vh = window.innerHeight;
  const top = Math.max(2, box.top - pad);
  const left = Math.max(2, box.left - pad);
  const right = Math.min(vw - 2, box.left + box.width + pad);
  const bottom = Math.min(vh - 2, box.top + box.height + pad);
  const w = Math.max(16, right - left), h = Math.max(16, bottom - top);
  const radius = Math.max(4, (box.radius || 0) + pad);
  const capAbove = top > 100;
  const capTop = capAbove ? top - 54 : bottom + 14;
  const capLeft = Math.max(12, Math.min(left, vw - 312));

  return (
    <div className="tour-spot" aria-hidden="true">
      <div className="tour-spot__ring" style={{ top, left, width: w, height: h, borderRadius: radius }} />
      <div className="tour-spot__arrow" style={{ top: capAbove ? capTop + 40 : capTop - 16, left: Math.min(left + w / 2 - 9, vw - 30) }}>
        {capAbove ? '▼' : '▲'}
      </div>
      {point.caption && (
        <div className={'tour-spot__cap' + (capAbove ? '' : ' tour-spot__cap--below')} style={{ top: capTop, left: capLeft }}>
          {point.caption}
        </div>
      )}
    </div>
  );
}
